/* Feedogo: show a waiting screen over the live player until the stream draws its first frame.
   The Feedogo dog logo, swinging its head and panting with its tongue, with "Live video is on its way" and a step line
   ("Connecting to the live stream" → "Opening the video") covers #liveVideo while a stream is selected but no picture
   is on screen yet, and is removed as soon as video appears. It sits *under* the player's own controls (prev/next arrows,
   room name, record, sound), so switching feeders keeps working while a stream is loading or stuck.
   Works for both players: own machines (flv.js on the <video>) and partner machines
   (Jessibuca canvas added by feedogo-hevc.js, which marks its box data-hevc-live="1" on the first frame).
   It only adds an overlay; it does not touch the player or the page's own code. */
(function () {
  'use strict';
  var FRAME_ID = 'feedogo-feeder-frame';
  var OVERLAY_ID = 'feedogo-live-waiting';
  var STYLE_ID = 'feedogo-live-waiting-style';

  // Feedogo dog logo (same as the supporters card), with the tongue out.
  var DOG_SVG =
    '<svg viewBox="40 20 650 560" width="100%" height="100%" aria-hidden="true">' +
    '<g fill="none" stroke="#1f403a" stroke-width="30" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M212 245 V385 Q212 440 150 440 Q75 440 72 370 Q70 300 140 190 Q250 45 365 45 Q480 45 590 190 Q660 300 658 370 Q655 440 580 440 Q515 440 515 385 V245"/>' +
    '<path d="M322 368 Q322 345 365 345 Q408 345 408 368 Q408 392 365 396 Q322 392 322 368 Z"/>' +
    '<path d="M215 455 Q245 495 292 495 Q362 495 365 405 Q368 495 438 495 Q485 495 515 455"/>' +
    '</g>' +
    '<path class="fg-tongue" d="M343 470 Q343 548 365 548 Q387 548 387 470 Z" fill="#ef7f86" stroke="#1f403a" stroke-width="16" stroke-linejoin="round"/>' +
    '<circle cx="297" cy="262" r="32" fill="#1f403a"/>' +
    '<path d="M392 272 L425 245 L458 272" fill="none" stroke="#d2e77b" stroke-width="26" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<g fill="#1f403a">' +
    '<circle cx="255" cy="432" r="9"/>' +
    '<circle cx="290" cy="415" r="9"/>' +
    '<circle cx="280" cy="452" r="9"/>' +
    '<circle cx="440" cy="415" r="9"/>' +
    '<circle cx="472" cy="432" r="9"/>' +
    '<circle cx="450" cy="452" r="9"/>' +
    '</g>' +
    '</svg>';

  var CSS =
    '#' + OVERLAY_ID + '{position:absolute;inset:0;z-index:2;display:flex;flex-direction:column;align-items:center;' +
      'justify-content:center;gap:8px;background:radial-gradient(circle at 50% 42%,#1d4a3f 0%,#0b201b 75%);' +
      'color:#f5f7f5;text-align:center;pointer-events:none;opacity:1;transition:opacity .35s ease;' +
      'font-family:Nunito,system-ui,-apple-system,sans-serif}' +
    '#' + OVERLAY_ID + ' [stroke="#1f403a"]{stroke:#f5f7f5}' +
    '#' + OVERLAY_ID + ' [fill="#1f403a"]{fill:#f5f7f5}' +
    '#' + OVERLAY_ID + ' .fg-sub{font-size:clamp(12px,3vw,14px);opacity:.75;min-height:1.3em}' +
    '#' + OVERLAY_ID + '.fg-hide{opacity:0}' +
    '#' + OVERLAY_ID + ' .fg-dog{width:clamp(72px,24%,140px);aspect-ratio:65/56;transform-origin:50% 90%;' +
      'animation:fgDogSwing 1.8s ease-in-out infinite}' +
    '#' + OVERLAY_ID + ' .fg-tongue{transform-box:fill-box;transform-origin:50% 0;animation:fgTongue .9s ease-in-out infinite}' +
    '#' + OVERLAY_ID + ' .fg-txt{font-size:clamp(16px,4.2vw,22px);font-weight:700;letter-spacing:.02em;margin-top:6px}' +
    '#' + OVERLAY_ID + ' .fg-dots span{animation:fgDot 1.2s infinite;opacity:.2;color:#d8f36c}' +
    '#' + OVERLAY_ID + ' .fg-dots span:nth-child(2){animation-delay:.2s}' +
    '#' + OVERLAY_ID + ' .fg-dots span:nth-child(3){animation-delay:.4s}' +
    '@keyframes fgDogSwing{0%,100%{transform:rotate(-12deg)}50%{transform:rotate(12deg)}}' +
    '@keyframes fgTongue{0%,100%{transform:scaleY(.35)}50%{transform:scaleY(1.15)}}' +
    '@keyframes fgDot{0%,100%{opacity:.2}50%{opacity:1}}' +
    '@media (prefers-reduced-motion:reduce){#' + OVERLAY_ID + ' .fg-dog,#' + OVERLAY_ID + ' .fg-tongue{animation-duration:3s}}';

  function streamUrl(w) {
    return String(w.__liveKey || '').split('|')[1] || '';
  }

  function hasPicture(d, video) {
    var box = d.querySelector('[data-hevc-box]');
    if (box) return box.getAttribute('data-hevc-live') === '1';
    return video.readyState >= 2 && video.videoWidth > 0;
  }

  function ensureOverlay(d, video) {
    var o = d.getElementById(OVERLAY_ID);
    if (o && o.parentNode === video.parentNode) return o;
    if (o) o.remove();
    if (!d.getElementById(STYLE_ID)) {
      var st = d.createElement('style');
      st.id = STYLE_ID;
      st.textContent = CSS;
      d.head.appendChild(st);
    }
    var parent = video.parentNode;
    if (d.defaultView.getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
    o = d.createElement('div');
    o.id = OVERLAY_ID;
    o.setAttribute('role', 'status');
    o.innerHTML = '<div class="fg-dog">' + DOG_SVG + '</div>' +
      '<div class="fg-txt">Live video is on its way<span class="fg-dots"><span>.</span><span>.</span><span>.</span></span></div>' +
      '<div class="fg-sub"></div>';
    // just above the picture, below the page's own controls (same z-index, earlier in the DOM)
    var before = parent.querySelector('.camera-top');
    if (before && before.parentNode === parent) parent.insertBefore(o, before); else parent.appendChild(o);
    return o;
  }

  function tick() {
    var f = document.getElementById(FRAME_ID);
    if (!f) return;
    try {
      var w = f.contentWindow, d = w && w.document;
      var video = d && d.getElementById('liveVideo');
      if (!video || !video.parentNode) return;
      var waiting = !!streamUrl(w) && !hasPicture(d, video);
      var o = d.getElementById(OVERLAY_ID);
      if (waiting) {
        o = ensureOverlay(d, video);
        var key = streamUrl(w);
        if (o.getAttribute('data-key') !== key || o.classList.contains('fg-hide')) { o.setAttribute('data-key', key); o.setAttribute('data-since', String(Date.now())); }
        var t = Date.now() - Number(o.getAttribute('data-since') || 0);
        var sub = o.querySelector('.fg-sub');
        var msg = t < 2500 ? 'Connecting to the live stream' : 'Opening the video';
        if (sub && sub.textContent !== msg) sub.textContent = msg;
        o.classList.remove('fg-hide');
        o.style.display = '';
      } else if (o && !o.classList.contains('fg-hide')) {
        o.classList.add('fg-hide');
        setTimeout(function () { if (o.classList.contains('fg-hide')) o.style.display = 'none'; }, 400);
      }
    } catch (e) {}
  }

  setInterval(tick, 200);
})();
